/**
 * Users hooks using React Relay
 *
 * These hooks provide access to user data through Relay's
 * normalized cache and automatic data management.
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  useLazyLoadQuery,
  usePaginationFragment,
  useQueryLoader,
  usePreloadedQuery,
  PreloadedQuery,
  fetchQuery,
} from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type {
  useUsersRelayQuery as UseUsersRelayQuery,
  useUsersRelayQuery$variables,
  UserFilterInput,
  UserSortInput,
} from './__generated__/useUsersRelayQuery.graphql';
import type { useUsersRelayDirectQuery as UseUsersRelayDirectQuery } from './__generated__/useUsersRelayDirectQuery.graphql';
import type { useUsersRelayByIdQuery } from './__generated__/useUsersRelayByIdQuery.graphql';
import type { useUsersRelayPaginationFragment$key } from './__generated__/useUsersRelayPaginationFragment.graphql';

// ============================================================================
// GraphQL Definitions
// ============================================================================

/**
 * Pagination fragment for users list
 */
const UsersListPaginationFragment = graphql`
  fragment useUsersRelayPaginationFragment on RootQueryType
  @refetchable(queryName: "useUsersRelayPaginationQuery")
  @argumentDefinitions(
    first: { type: "Int", defaultValue: 100 }
    after: { type: "String" }
    filter: { type: "UserFilterInput" }
    sort: { type: "[UserSortInput!]" }
    search: { type: "String" }
  ) {
    listUsers(
      first: $first
      after: $after
      filter: $filter
      sort: $sort
      search: $search
    )
      @connection(
        key: "useUsersRelayPaginationFragment_listUsers"
        filters: ["filter", "sort"]
      ) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          phone
          email
          firstName
          lastName
          fullName
          createdOn
          delinquent
          authStatus
          language
          region
          userSystems {
            id
            blacklisted
            hidden
            joined
            rejected
            removed
            acceptedTosOn
            systemId
          }
          userMemberships(limit: 1) {
            id
          }
        }
      }
    }
  }
`;

/**
 * Query for paginated users list (uses fragment for Suspense hooks)
 */
const UsersListQuery = graphql`
  query useUsersRelayQuery(
    $first: Int
    $after: String
    $filter: UserFilterInput
    $sort: [UserSortInput!]
    $search: String
  ) {
    ...useUsersRelayPaginationFragment
      @arguments(
        first: $first
        after: $after
        filter: $filter
        sort: $sort
        search: $search
      )
  }
`;

/**
 * Direct query for users list (for non-Suspense fetchQuery usage)
 * This query doesn't use fragments so fetchQuery returns the actual data
 */
const UsersListDirectQuery = graphql`
  query useUsersRelayDirectQuery(
    $first: Int
    $after: String
    $filter: UserFilterInput
    $sort: [UserSortInput!]
    $search: String
  ) {
    listUsers(
      first: $first
      after: $after
      filter: $filter
      sort: $sort
      search: $search
    ) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          phone
          email
          firstName
          lastName
          fullName
          createdOn
          delinquent
          authStatus
          language
          region
          userSystems {
            id
            blacklisted
            hidden
            joined
            rejected
            removed
            acceptedTosOn
            systemId
          }
          userMemberships(limit: 1) {
            id
          }
        }
      }
    }
  }
`;

/**
 * Query for single user by ID
 */
const UserByIdQuery = graphql`
  query useUsersRelayByIdQuery($id: ID!) {
    getUser(id: $id) {
      id
      phone
      email
      firstName
      lastName
      fullName
      createdOn
      delinquent
      authStatus
      language
      region
      organizationIds
      systemIds
      userSystems {
        id
        blacklisted
        hidden
        joined
        rejected
        removed
        acceptedTosOn
        systemId
      }
      userMemberships(limit: 1) {
        id
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export type { UserFilterInput, UserSortInput };

/**
 * User system type from GraphQL
 */
export interface UserSystemGraphQL {
  id: string;
  blacklisted?: boolean | null;
  hidden?: boolean | null;
  joined?: string | null;
  rejected?: boolean | null;
  removed?: boolean | null;
  acceptedTosOn?: string | null;
  systemId?: string | null;
}

/**
 * User type from GraphQL - matches the fragment fields
 */
export interface UserGraphQL {
  id: string;
  phone: string;
  email?: string | null;
  firstName: string;
  lastName: string;
  fullName?: string | null;
  createdOn?: string | null;
  delinquent?: boolean | null;
  authStatus?: number | null;
  language?: string | null;
  region: string;
  organizationIds?: readonly string[] | null;
  systemIds?: readonly string[] | null;
  userSystems?: UserSystemGraphQL[];
  userMemberships?: Array<{ id: string }>;
}

/**
 * Simplified filter options for common use cases
 */
export interface UserQueryFilter {
  systemId?: string;
  /**
   * Filter by multiple systems (org view's System multi-select). Applied via a
   * nested `userSystems.systemId.in` clause. Additive/backward-compatible with
   * the single `systemId` above.
   */
  systemIds?: string[];
  organizationId?: string;
  /**
   * Free-text search from the list page's search bar. Matched against the user's
   * name, and — when the term is all digits — against their phone number.
   *
   * Email is deliberately absent: `UserFilterInput` exposes no `email` field
   * because `User.email` is a server-side calculation, so it cannot be filtered
   * on. Email search needs a `search` argument on `listUsers` (tracked in ant);
   * once that exists this should switch to passing the term straight through.
   */
  search?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  delinquent?: boolean;
  // Nested filters via userSystems
  blacklisted?: boolean;
  hidden?: boolean;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for fetching users list using Relay
 *
 * This hook uses Relay's useLazyLoadQuery which suspends while loading.
 * Wrap with Suspense boundary.
 *
 * @example
 * function UserList() {
 *   const { data, loadNext, hasNext, isLoadingNext } = useUsersRelay({
 *     systemId: 'uuid',
 *   });
 *
 *   return (
 *     <>
 *       {data.map(user => <div key={user.id}>{user.fullName}</div>)}
 *       {hasNext && <button onClick={() => loadNext(100)}>Load More</button>}
 *     </>
 *   );
 * }
 *
 * // With Suspense
 * <Suspense fallback={<Loading />}>
 *   <UserList />
 * </Suspense>
 */
export function useUsersRelay(
  filter?: UserQueryFilter,
  options?: {
    limit?: number;
    sort?: UserSortInput[];
  }
) {
  const limit = options?.limit ?? 100;
  const sort = options?.sort ?? [{ field: 'CREATED_ON', order: 'DESC' }];

  const variables: useUsersRelayQuery$variables = {
    first: limit,
    filter: buildRelayFilter(filter),
    sort,
    search: searchArgument(filter),
  };

  // Initial query - suspends while loading
  const queryData = useLazyLoadQuery<UseUsersRelayQuery>(UsersListQuery, variables);

  // Pagination fragment for load more
  const { data, loadNext, hasNext, isLoadingNext, refetch } = usePaginationFragment<
    UseUsersRelayQuery,
    useUsersRelayPaginationFragment$key
  >(UsersListPaginationFragment, queryData);

  // Extract nodes from edges
  const nodes = data.listUsers?.edges?.map((edge) => edge?.node).filter(Boolean) ?? [];

  return {
    data: nodes as UserGraphQL[],
    count: data.listUsers?.count,
    pageInfo: data.listUsers?.pageInfo,
    loadNext,
    hasNext,
    isLoadingNext,
    refetch,
  };
}

/**
 * Hook for fetching a single user by ID using Relay
 *
 * Uses Suspense - wrap with Suspense boundary
 *
 * @example
 * function UserDetail({ id }: { id: string }) {
 *   const user = useUserByIdRelay(id);
 *   return <div>{user?.fullName}</div>;
 * }
 */
export function useUserByIdRelay(userId: string) {
  const data = useLazyLoadQuery<useUsersRelayByIdQuery>(UserByIdQuery, {
    id: userId,
  });

  return data.getUser as UserGraphQL | null;
}

// ============================================================================
// Non-Suspense Hooks (with loading state)
// ============================================================================

/**
 * Hook that returns a query loader for users list
 *
 * This provides explicit loading state without requiring Suspense.
 * Call `loadQuery` to start fetching, then pass `queryRef` to a child component.
 *
 * @example
 * function UserContainer() {
 *   const { queryRef, loadQuery, isLoading } = useUsersRelayLoader();
 *
 *   useEffect(() => {
 *     loadQuery({ systemId: 'uuid' });
 *   }, []);
 *
 *   if (isLoading || !queryRef) {
 *     return <Loading />;
 *   }
 *
 *   return <UserListWithData queryRef={queryRef} />;
 * }
 *
 * function UserListWithData({ queryRef }) {
 *   const data = useUsersRelayPreloaded(queryRef);
 *   return <div>{data.map(u => u.fullName)}</div>;
 * }
 */
export function useUsersRelayLoader(initialFilter?: UserQueryFilter) {
  const [queryRef, loadQuery, disposeQuery] = useQueryLoader<UseUsersRelayQuery>(
    UsersListQuery,
    initialFilter
      ? {
          first: 100,
          filter: buildRelayFilter(initialFilter),
          sort: [{ field: 'CREATED_ON', order: 'DESC' }],
          search: searchArgument(initialFilter),
        }
      : undefined
  );

  const load = (
    filter?: UserQueryFilter,
    options?: { limit?: number; sort?: UserSortInput[] }
  ) => {
    const limit = options?.limit ?? 100;
    const sort = options?.sort ?? [{ field: 'CREATED_ON', order: 'DESC' }];

    loadQuery({
      first: limit,
      filter: buildRelayFilter(filter),
      sort,
      search: searchArgument(filter),
    });
  };

  return {
    queryRef,
    loadQuery: load,
    disposeQuery,
    isLoading: queryRef === null,
  };
}

/**
 * Hook to read data from a preloaded users query
 *
 * Use with useUsersRelayLoader. Still suspends, but the data
 * is already loading so suspension is minimal.
 */
export function useUsersRelayPreloaded(queryRef: PreloadedQuery<UseUsersRelayQuery>) {
  const queryData = usePreloadedQuery<UseUsersRelayQuery>(UsersListQuery, queryRef);

  const { data, loadNext, hasNext, isLoadingNext, refetch } = usePaginationFragment<
    UseUsersRelayQuery,
    useUsersRelayPaginationFragment$key
  >(UsersListPaginationFragment, queryData);

  const nodes = data.listUsers?.edges?.map((edge) => edge?.node).filter(Boolean) ?? [];

  return {
    data: nodes as UserGraphQL[],
    count: data.listUsers?.count,
    pageInfo: data.listUsers?.pageInfo,
    loadNext,
    hasNext,
    isLoadingNext,
    refetch,
  };
}

/**
 * Hook for user by ID with explicit loading state (no Suspense required)
 *
 * @example
 * function UserDetail({ id }: { id: string }) {
 *   const { queryRef, loadQuery, isLoading } = useUserByIdRelayLoader();
 *
 *   useEffect(() => {
 *     loadQuery(id);
 *   }, [id]);
 *
 *   if (isLoading || !queryRef) return <Loading />;
 *
 *   return <UserDetailContent queryRef={queryRef} />;
 * }
 */
export function useUserByIdRelayLoader(initialId?: string) {
  const [queryRef, loadQuery, disposeQuery] = useQueryLoader<useUsersRelayByIdQuery>(
    UserByIdQuery,
    initialId ? { id: initialId } : undefined
  );

  const load = (userId: string) => {
    loadQuery({ id: userId });
  };

  return {
    queryRef,
    loadQuery: load,
    disposeQuery,
    isLoading: queryRef === null,
  };
}

/**
 * Hook to read data from a preloaded user-by-id query
 */
export function useUserByIdRelayPreloaded(
  queryRef: PreloadedQuery<useUsersRelayByIdQuery>
) {
  const data = usePreloadedQuery<useUsersRelayByIdQuery>(UserByIdQuery, queryRef);
  return data.getUser as UserGraphQL | null;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * The free-text term the list page's search box produces, handed to ant's
 * `search` argument on `listUsers`.
 *
 * ant matches it against first name, last name, phone cast to text, and the
 * user's emails. That last one is why this exists: `User.email` is a
 * server-side calculation with no filter input, so email was simply
 * unsearchable while this hook built its own OR'd filter clauses. Phone was
 * exact-match only for the same reason — `phone` is a `string_integer` whose
 * filter offers no contains — and casting happens server-side now, so partial
 * numbers work.
 *
 * Digits are stripped from anything that looks like a phone so a term typed as
 * `484 667-3054` still matches a stored `14846673054`.
 */
export function searchArgument(filter?: UserQueryFilter): string | undefined {
  const term = filter?.search?.trim();
  if (!term) return undefined;
  return /^[\d\s\-()+]+$/.test(term) ? term.replace(/\D/g, '') : term;
}

/**
 * The dedicated phone column filter, not the free-text search box — that now
 * goes to ant's `search` argument, which casts phone to text and matches
 * partials. This stays exact-match because `phone` is a `string_integer`
 * server-side and its filter input offers no ilike/contains.
 *
 * Two things this has to get right:
 *
 * 1. Phones are stored WITH the country code — the overwhelming majority as 11
 *    digits beginning `1`. An admin types the 10 digits they know, so matching
 *    only the raw term finds essentially nobody. Both the bare term and the
 *    `1`-prefixed form are offered via `in`, which is one clause either way.
 * 2. The term must be length-bounded. `string_integer` casts to int8, so a long
 *    digit run overflows and nulls the WHOLE listUsers payload rather than
 *    returning nothing — and the list hook's catch sets an error without clearing
 *    data, leaving the previous page's rows on screen as if they matched. 15 is
 *    the E.164 maximum, so nothing real is excluded.
 */
const MAX_PHONE_DIGITS = 15;
const DEFAULT_COUNTRY_CODE = '1';

function phoneSearchClauses(term: string): UserFilterInput[] {
  if (!/^\d+$/.test(term) || term.length > MAX_PHONE_DIGITS) return [];

  const candidates = [term];
  // A 10-digit entry is a national number missing its country code.
  if (term.length === 10) {
    candidates.push(`${DEFAULT_COUNTRY_CODE}${term}`);
  }
  return [{ phone: { in: candidates } }];
}

/**
 * Builds a Relay/GraphQL UserFilterInput from the simplified filter interface
 */
function buildRelayFilter(filter?: UserQueryFilter): UserFilterInput | undefined {
  if (!filter) return undefined;

  const relayFilter: UserFilterInput = {};

  // `phone` is a string_integer server-side: its filter input has no
  // ilike/contains, and a non-numeric or oversized `eq` fails to cast (nulling the
  // whole payload). phoneSearchClauses handles the digits, the length bound and
  // the missing-country-code case.
  const phoneClauses = phoneSearchClauses(filter.phone?.replace(/\D/g, '') ?? '');
  if (phoneClauses.length > 0) {
    relayFilter.phone = phoneClauses[0].phone;
  }
  if (filter.firstName) {
    if (filter.firstName.includes('%')) {
      relayFilter.firstName = { ilike: filter.firstName };
    } else {
      relayFilter.firstName = { eq: filter.firstName };
    }
  }
  if (filter.lastName) {
    if (filter.lastName.includes('%')) {
      relayFilter.lastName = { ilike: filter.lastName };
    } else {
      relayFilter.lastName = { eq: filter.lastName };
    }
  }
  if (filter.fullName) {
    if (filter.fullName.includes('%')) {
      relayFilter.fullName = { ilike: filter.fullName };
    } else {
      relayFilter.fullName = { eq: filter.fullName };
    }
  }
  if (filter.delinquent !== undefined) {
    relayFilter.delinquent = { eq: filter.delinquent };
  }
  // Filter by system/org/blacklisted/hidden via nested userSystems filter
  {
    const userSystemsFilter: Record<string, unknown> =
      (relayFilter.userSystems as Record<string, unknown>) ?? {};
    if (filter.systemId) {
      userSystemsFilter.systemId = { eq: filter.systemId };
    } else if (filter.systemIds && filter.systemIds.length > 0) {
      userSystemsFilter.systemId = { in: filter.systemIds };
    }
    if (filter.organizationId) {
      userSystemsFilter.organizationId = { eq: filter.organizationId };
    }
    if (filter.blacklisted !== undefined) {
      userSystemsFilter.blacklisted = { eq: filter.blacklisted };
    }
    if (filter.hidden !== undefined) {
      userSystemsFilter.hidden = { eq: filter.hidden };
    }
    if (Object.keys(userSystemsFilter).length > 0) {
      relayFilter.userSystems = userSystemsFilter;
    }
  }

  return Object.keys(relayFilter).length > 0 ? relayFilter : undefined;
}

// ============================================================================
// React Query-like Interface (No Suspense)
// ============================================================================

/**
 * Hook that provides React Query-like interface for users data
 *
 * This hook does NOT use Suspense - it provides isLoading, data, refetch etc.
 * similar to React Query's useQuery.
 *
 * @example
 * const { data, isLoading, refetch, isFetched } = useUsersRelayQuery({
 *   systemId: 'uuid',
 * });
 *
 * if (isLoading) return <Loading />;
 * return <div>{data?.map(u => u.fullName)}</div>;
 */
export function useUsersRelayQuery(
  filter?: UserQueryFilter,
  options?: {
    limit?: number;
    after?: string | null;
    sort?: UserSortInput[];
    enabled?: boolean;
  }
) {
  const [data, setData] = useState<UserGraphQL[] | null>(null);
  const [count, setCount] = useState<number | undefined>(undefined);
  const [hasMore, setHasMore] = useState(false);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0); // For manual refetch trigger

  const limit = options?.limit ?? 100;
  const after = options?.after ?? undefined;
  const sort = useMemo(
    () => options?.sort ?? [{ field: 'CREATED_ON' as const, order: 'DESC' as const }],
    [options?.sort]
  );
  const enabled = options?.enabled !== false;

  // Serialize filter to stable string for dependency comparison
  const filterKey = useMemo(() => JSON.stringify(filter), [filter]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        if (isFetched) {
          setIsRefetching(true);
        } else {
          setIsLoading(true);
        }

        const variables: useUsersRelayQuery$variables = {
          first: limit,
          after,
          filter: buildRelayFilter(filter),
          sort,
          search: searchArgument(filter),
        };

        // Use direct query (not fragment-based) for fetchQuery compatibility
        const result = await fetchQuery<UseUsersRelayDirectQuery>(
          relayEnvironment,
          UsersListDirectQuery,
          variables
        ).toPromise();

        if (cancelled) return;

        if (result) {
          const nodes =
            result.listUsers?.edges?.map((edge) => edge?.node).filter(Boolean) ?? [];
          setData(nodes as UserGraphQL[]);
          setCount(result.listUsers?.count ?? undefined);
          setHasMore(result.listUsers?.pageInfo?.hasNextPage ?? false);
          setEndCursor(result.listUsers?.pageInfo?.endCursor ?? null);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error('Failed to fetch users'));
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
          setIsRefetching(false);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, filterKey, limit, after, sort, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return {
    data,
    count,
    hasMore,
    endCursor,
    isLoading,
    isFetched,
    isRefetching,
    error,
    refetch,
  };
}
